import type { MailBody } from '@/api'
import { api } from '@/api'
import type { MailboxContacts, MailboxSignature, MailboxSource } from '@/components/MailboxPanel.vue'
import { requireAiAccount } from '@/lib/aiAccountGuard'

/**
 * The signed-in person's own mailbox (#887), over `/v1/mail`, which only the person's own session reaches: every
 * command the person may give their own mail and the one-click buttons their inbox offers, each a task that declares
 * a mailbox action, started over their mailbox or cloned from the Catalog first, and their contacts (#1114), which
 * nobody else reaches, and their signature (#1115). Nothing here is gated: a person's mail is theirs. A button's task is model work, though, so it asks the AI account guard like every other run the
 * portal starts (#1796).
 */
export function myMailboxSource(): MailboxSource {
    return {
        list: query => api.myMail(query),
        open: emailId => api.myMailMessage(emailId),
        attachmentUrl: (emailId, attachmentId) => api.myMailAttachmentUrl(emailId, attachmentId),
        actions: {
            reply: (emailId, body, attachments) =>
                api.myMailReply(emailId, attachments.length > 0 ? { ...body, attachments } : body),
            forward: (emailId, recipients, body, attachments) =>
                api.myMailForward(emailId, {
                    ...recipients,
                    ...forwardNote(body),
                    ...(attachments.length > 0 ? { attachments } : {}),
                }),
            send: body => api.myMailSend(body),
            setHandled: (emailId, handledState, snoozedUntil) =>
                api.myMailSetHandled(emailId, handledState, snoozedUntil),
            setLabels: (emailId, labels) => api.myMailSetLabels(emailId, labels),
            remove: emailId => api.myMailDelete(emailId),
            move: (emailId, target) => api.myMailMove(emailId, target),
            destinations: async () => (await api.mailboxDestinations()).filter(mailbox => !mailbox.self),
        },
        contacts: myContacts(),
        signature: mySignature(),
        offers: () => api.myMailActions(),
        startAction: async (taskId, emailId) => {
            if (!(await requireAiAccount())) return null
            return api.startMyMailAction(taskId, emailId)
        },
        cloneAction: catalogTaskId => api.cloneCatalogTask(catalogTaskId),
        scheduleAction: {
            save: (taskId, cron) => api.scheduleMyMailAction(taskId, { cron }),
            clear: taskId => api.unscheduleMyMailAction(taskId),
        },
        messageUrl: emailId => `/inbox/${emailId}`,
        listUrl: '/inbox',
    }
}

/**
 * A member's mailbox: anybody who reads the member reads it, with the one-click buttons of the tasks that declare a
 * mailbox action and run as a role this member holds, each started as an ordinary run. An enterprise owner (`canAct`)
 * also answers, forwards, files and deletes its mail as a mail client would: what they send leaves as the member, as
 * a member's run sends once the owner approves it, keeps its contacts (#1114) and sets its signature (#1115). Everybody else reads it only, and
 * the API refuses them anyway.
 */
export function memberMailboxSource(memberId: string, canAct: boolean): MailboxSource {
    return {
        list: query => api.memberEmails(memberId, query),
        open: emailId => api.memberEmail(memberId, emailId),
        attachmentUrl: (emailId, attachmentId) => api.memberEmailAttachmentUrl(memberId, emailId, attachmentId),
        ...(canAct
            ? {
                  actions: {
                      reply: (emailId, body, attachments) =>
                          api.memberEmailReply(
                              memberId,
                              emailId,
                              attachments.length > 0 ? { ...body, attachments } : body,
                          ),
                      forward: (emailId, recipients, body, attachments) =>
                          api.memberEmailForward(memberId, emailId, {
                              ...recipients,
                              ...forwardNote(body),
                              ...(attachments.length > 0 ? { attachments } : {}),
                          }),
                      send: body => api.memberEmailSend(memberId, body),
                      setHandled: (emailId, handledState, snoozedUntil) =>
                          api.memberEmailSetHandled(memberId, emailId, handledState, snoozedUntil),
                      setLabels: (emailId, labels) => api.memberEmailSetLabels(memberId, emailId, labels),
                      remove: emailId => api.memberEmailDelete(memberId, emailId),
                      move: (emailId, target) => api.memberEmailMove(memberId, emailId, target),
                      destinations: async () =>
                          (await api.mailboxDestinations()).filter(
                              mailbox => !(mailbox.kind === 'member' && mailbox.id === memberId),
                          ),
                  },
                  contacts: memberContacts(memberId),
                  signature: memberSignature(memberId),
              }
            : {}),
        offers: () => api.memberMailboxActions(memberId),
        startAction: async (taskId, emailId) => {
            // #1796: a run is model work, so without an AI account nothing is sent and the prompt opens.
            if (!(await requireAiAccount())) return null
            return api.startRun(taskId, emailId === undefined ? {} : { emailId })
        },
        messageUrl: emailId => `/members/${memberId}/mail/${emailId}`,
        listUrl: `/members/${memberId}`,
    }
}

/** The person's own contacts: theirs alone, over `/v1/mail/contacts`. */
function myContacts(): MailboxContacts {
    return {
        list: query => api.myMailContacts(query),
        save: body => api.saveMyMailContact(body),
        rename: (id, name) => api.renameMyMailContact(id, name),
        remove: id => api.removeMyMailContact(id),
        import: file => api.importMyMailContacts(file),
        exportUrl: format => api.myMailContactsExportUrl(format),
    }
}

/** A member's contacts, for an enterprise owner: the API refuses anybody else. */
function memberContacts(memberId: string): MailboxContacts {
    return {
        list: query => api.memberMailContacts(memberId, query),
        save: body => api.saveMemberMailContact(memberId, body),
        rename: (id, name) => api.renameMemberMailContact(memberId, id, name),
        remove: id => api.removeMemberMailContact(memberId, id),
        import: file => api.importMemberMailContacts(memberId, file),
        exportUrl: format => api.memberMailContactsExportUrl(memberId, format),
    }
}

/**
 * A forward's note, when one was written, and the writer's choice about the signature (#1115) either way: a forward
 * with no note still leaves without the signature when it was left off. A reply's `quote` (#1116) is never a forward's.
 */
function forwardNote(body: MailBody): Partial<MailBody> {
    const signature = body.signature === false ? { signature: false } : {}
    if (body.text.trim()) {
        return { text: body.text, ...(body.html === undefined ? {} : { html: body.html }), ...signature }
    }
    return signature
}

/**
 * The person's own signature: theirs alone, over `/v1/mail/signature`, and their extra addresses' own signatures
 * (#1122), which they set over `/v1/mail/aliases`.
 */
function mySignature(): MailboxSignature {
    return {
        read: () => api.myMailSignature(),
        save: body => api.saveMyMailSignature(body),
        clear: () => api.clearMyMailSignature(),
        addresses: {
            list: async () => (await api.myMailAliases()).aliases,
            save: async (aliasId, body) => (await api.saveMyMailAliasSignature(aliasId, body)).aliases,
            clear: async aliasId => (await api.clearMyMailAliasSignature(aliasId)).aliases,
        },
    }
}

/**
 * A member's signature, for an enterprise owner: the API refuses anybody else. Its extra addresses' own signatures
 * (#1122) are the owner's too, over the routes the member's addresses are managed by.
 */
function memberSignature(memberId: string): MailboxSignature {
    return {
        read: () => api.memberMailSignature(memberId),
        save: body => api.saveMemberMailSignature(memberId, body),
        clear: () => api.clearMemberMailSignature(memberId),
        addresses: {
            list: async () => (await api.mailAliases(memberId)).aliases,
            save: async (aliasId, body) => (await api.saveMailAliasSignature(memberId, aliasId, body)).aliases,
            clear: async aliasId => (await api.clearMailAliasSignature(memberId, aliasId)).aliases,
        },
    }
}
